/**
 * Supervision (SU-7, W7, ADR-0049): an admin picks a live call of its
 * organisation and joins it, muted (Écouter) or heard by the caller only
 * (Chuchoter). Twilio calls the admin's phone; hanging up or « Quitter »
 * leaves without ending the call. Behind the organisation flag
 * supervision_live, off by default, and blocked for production until tested
 * in staging (CLAUDE.md TwiML rule): the page says so.
 *
 * Only calls inside the caller's standing conference can be joined; the
 * others show why. The list refreshes every 5 s.
 *
 * Server: GET /api/supervision/calls, POST/PATCH/DELETE /api/supervision/sessions.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ear, Loader2, MessageCircle, PhoneOff, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { ApiErrorException } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ErrorLine, PlatformPageHeader } from '@/components/backoffice/platform/PlatformBits';
import { showErrorToast } from '@/lib/errorToast';
import { useAuthStore } from '@/stores/authStore';
import { currentLocale } from '@/i18n/locale';
import { joinCall, leaveCall, listLiveCalls, switchSupervisionMode, type LiveCall, type SupervisionMode } from '@/api/endpoints/permissions';

const PHONE_KEY = 'xdial.supervision.phone';
const E164 = /^\+[1-9]\d{6,14}$/;
const LIVE_KEY = ['supervision', 'calls'] as const;
const NO_ORGANISATION_CODES = new Set(['NO_ACTIVE_MEMBERSHIP', 'NO_TENANT']);

function readPhone(): string {
  try { return sessionStorage.getItem(PHONE_KEY) ?? ''; } catch { return ''; }
}
function writePhone(v: string): void {
  try { sessionStorage.setItem(PHONE_KEY, v); } catch { /* convenience for this tab only (sessionStorage: the number is not kept) */ }
}

function since(iso: string | null): string {
  if (!iso) return '—';
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  const m = Math.floor(s / 60);
  return `${m.toLocaleString(currentLocale())}:${String(s % 60).padStart(2, '0')}`;
}

export default function AdminSupervision() {
  const { t } = useTranslation(['admin', 'common']);
  const qc = useQueryClient();
  const [phone, setPhone] = useState(readPhone);
  const phoneOk = E164.test(phone.trim());

  // QA-W7-4: supervision is about the active organisation; without one the
  // server answers NO_ACTIVE_MEMBERSHIP. Ask to choose one instead.
  const hasOrganisation = useAuthStore((st) => !!st.activeMembership?.organisationId);
  const live = useQuery({ queryKey: LIVE_KEY, queryFn: listLiveCalls, refetchInterval: 5000, retry: false, enabled: hasOrganisation });
  const errorCode = live.error instanceof ApiErrorException ? live.error.code : '';
  const noOrganisation = !hasOrganisation || NO_ORGANISATION_CODES.has(errorCode);
  const disabled = !noOrganisation && errorCode === 'SUPERVISION_DISABLED';
  const refresh = () => void qc.invalidateQueries({ queryKey: LIVE_KEY });

  const join = useMutation({ mutationFn: (v: { call: LiveCall; mode: SupervisionMode }) => joinCall({ call_id: v.call.call_id, mode: v.mode, phone: phone.trim() }) });
  const sw = useMutation({ mutationFn: (v: { id: string; mode: SupervisionMode }) => switchSupervisionMode(v.id, v.mode) });
  const leave = useMutation({ mutationFn: (id: string) => leaveCall(id) });
  const busy = join.isPending || sw.isPending || leave.isPending;

  const onJoin = (call: LiveCall, mode: SupervisionMode) => {
    writePhone(phone.trim());
    join.mutate({ call, mode }, {
      onSuccess: () => { toast.success(t('admin:supervision.joined', { agent: call.agent_name || call.agent_email })); refresh(); },
      onError: (err) => showErrorToast(err),
    });
  };

  return (
    <div className="mx-auto max-w-5xl space-y-5 p-4 sm:p-6">
      <PlatformPageHeader
        title={t('admin:superAdminLayout.nav.supervision')}
        subtitle={t('admin:supervision.subtitle')}
        actions={!disabled && !noOrganisation && <Button type="button" variant="outline" size="sm" onClick={refresh} className="min-h-11"><RefreshCw className="mr-1 h-4 w-4" aria-hidden />{t('admin:supervision.refresh')}</Button>}
      />

      <p role="note" className="rounded-vf border px-3 py-2 text-sm" style={{ borderColor: 'var(--vf-orange)', background: 'var(--vf-orange-bg)' }}>
        {t('admin:supervision.stagingNotice')}
      </p>

      {disabled && (
        <section className="space-y-3 rounded-vf border p-6 text-center" style={{ borderColor: 'var(--vf-border)' }}>
          <h2 className="text-base font-semibold">{t('admin:supervision.disabledTitle')}</h2>
          <p className="text-sm" style={{ color: 'var(--vf-text-2)' }}>{t('admin:supervision.disabledBody')}</p>
          <Button asChild variant="outline"><Link to="/AdminPermissions">{t('admin:supervision.openPermissions')}</Link></Button>
        </section>
      )}

      {noOrganisation && (
        <section data-testid="supervision-no-organisation" className="space-y-3 rounded-vf border p-6 text-center" style={{ borderColor: 'var(--vf-border)' }}>
          <h2 className="text-base font-semibold">{t('admin:supervision.noOrganisationTitle')}</h2>
          <p className="text-sm" style={{ color: 'var(--vf-text-2)' }}>{t('admin:supervision.noOrganisationBody')}</p>
          <Button asChild variant="outline"><Link to="/AdminOrganisations">{t('admin:supervision.openOrganisations')}</Link></Button>
        </section>
      )}

      {!disabled && !noOrganisation && (
        <>
          <div className="flex flex-col gap-1 text-sm sm:max-w-sm">
            <label htmlFor="sup-phone" className="font-medium">{t('admin:supervision.phone')}</label>
            <Input
              id="sup-phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="+33612345678"
              value={phone}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.target.value)}
              aria-invalid={phone !== '' && !phoneOk ? true : undefined}
              aria-describedby="sup-phone-hint"
              className="h-11"
            />
            <span id="sup-phone-hint" className="text-xs" style={{ color: phone !== '' && !phoneOk ? 'var(--vf-fill-danger)' : 'var(--vf-text-2)' }}>
              {phone !== '' && !phoneOk ? t('admin:supervision.phoneInvalid') : t('admin:supervision.phoneHint')}
            </span>
          </div>

          {live.isLoading && (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="h-6 w-6 animate-spin motion-reduce:animate-none" style={{ color: 'var(--vf-accent)' }} aria-label={t('common:actions.loading')} />
            </div>
          )}
          {live.isError && <ErrorLine message={t('admin:supervision.loadError')} error={live.error} onRetry={refresh} />}

          {live.data && live.data.calls.length === 0 && (
            <section className="space-y-3 rounded-vf border p-6 text-center" style={{ borderColor: 'var(--vf-border)' }}>
              <p className="text-sm" style={{ color: 'var(--vf-text-2)' }}>{t('admin:supervision.empty')}</p>
              <Button type="button" variant="outline" onClick={refresh}>{t('admin:supervision.refresh')}</Button>
            </section>
          )}

          {live.data && live.data.calls.length > 0 && (
            <div className="overflow-x-auto rounded-vf border" style={{ borderColor: 'var(--vf-border)' }}>
              <table className="w-full min-w-[640px] text-sm" aria-busy={busy || undefined}>
                <caption className="sr-only">{t('admin:supervision.caption')}</caption>
                <thead>
                  <tr style={{ background: 'var(--vf-surface-2)' }}>
                    <th scope="col" className="px-3 py-2 text-left font-medium">{t('admin:supervision.columns.agent')}</th>
                    <th scope="col" className="px-3 py-2 text-left font-medium">{t('admin:supervision.columns.prospect')}</th>
                    <th scope="col" className="px-3 py-2 text-left font-medium">{t('admin:supervision.columns.duration')}</th>
                    <th scope="col" className="px-3 py-2 text-right font-medium">{t('admin:supervision.columns.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {live.data.calls.map((c) => (
                    <tr key={c.call_id} className="border-t" style={{ borderColor: 'var(--vf-border)' }}>
                      <td className="px-3 py-2">{c.agent_name || c.agent_email}</td>
                      <td className="px-3 py-2">{c.prospect_name || '—'}</td>
                      <td className="px-3 py-2 tabular-nums">{since(c.connected_at)}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          {!c.supervisable && <span className="text-xs" style={{ color: 'var(--vf-text-2)' }}>{t('admin:supervision.noConference')}</span>}
                          {c.supervisable && !c.session && (
                            <>
                              <Button type="button" size="sm" variant="outline" className="min-h-11" disabled={!phoneOk || busy} onClick={() => onJoin(c, 'listen')}>
                                <Ear className="mr-1 h-4 w-4" aria-hidden />{t('admin:supervision.listen')}
                              </Button>
                              <Button type="button" size="sm" variant="outline" className="min-h-11" disabled={!phoneOk || busy} onClick={() => onJoin(c, 'whisper')}>
                                <MessageCircle className="mr-1 h-4 w-4" aria-hidden />{t('admin:supervision.whisper')}
                              </Button>
                            </>
                          )}
                          {c.session && (
                            <>
                              <span className="text-xs font-medium">{t(`admin:supervision.mode.${c.session.mode}`)}</span>
                              <Button
                                type="button" size="sm" variant="outline" className="min-h-11" disabled={busy}
                                onClick={() => sw.mutate({ id: c.session!.id, mode: c.session!.mode === 'listen' ? 'whisper' : 'listen' }, { onSuccess: refresh, onError: (err) => showErrorToast(err) })}
                              >
                                {t(c.session.mode === 'listen' ? 'admin:supervision.toWhisper' : 'admin:supervision.toListen')}
                              </Button>
                              <Button
                                type="button" size="sm" variant="outline" className="min-h-11" disabled={busy}
                                onClick={() => leave.mutate(c.session!.id, { onSuccess: () => { toast.success(t('admin:supervision.left')); refresh(); }, onError: (err) => showErrorToast(err) })}
                              >
                                <PhoneOff className="mr-1 h-4 w-4" aria-hidden />{t('admin:supervision.leave')}
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
